import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Users, Target, Eye, ThumbsUp, Bookmark, Video, MousePointer, ShoppingCart } from "lucide-react";

const stageConfig = {
  awareness: {
    title: "TOPO - Awareness",
    objective: "Educar sobre o produto/serviço",
    icon: Eye,
    color: "text-blue-500",
    bgColor: "bg-blue-50 dark:bg-blue-950",
    borderColor: "border-blue-200 dark:border-blue-800",
  },
  consideration: {
    title: "MEIO - Consideration",
    objective: "Capturar leads qualificados",
    icon: MousePointer,
    color: "text-orange-500",
    bgColor: "bg-orange-50 dark:bg-orange-950",
    borderColor: "border-orange-200 dark:border-orange-800",
  },
  conversion: {
    title: "FUNDO - Conversion",
    objective: "Fechar vendas",
    icon: ShoppingCart,
    color: "text-green-500",
    bgColor: "bg-green-50 dark:bg-green-950",
    borderColor: "border-green-200 dark:border-green-800",
  },
};

export default function FunnelStageCard({ stage }) {
  const config = stageConfig[stage.funnel_stage];
  if (!config) return null;

  const Icon = config.icon;

  return (
    <Card className={`${config.borderColor} border-2`}>
      <CardHeader className={config.bgColor}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon className={`h-6 w-6 ${config.color}`} />
            <div>
              <CardTitle className="text-xl">{config.title}</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">Objetivo: {config.objective}</p>
            </div>
          </div>
          <Badge variant="secondary" className="text-lg">
            ${stage.spend?.toLocaleString() || 0} ({stage.spend_percent || 0}%)
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="p-6 space-y-4">
        {/* Awareness Metrics */}
        {stage.funnel_stage === 'awareness' && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <MetricItem 
                label="Reach" 
                value={stage.reach?.toLocaleString() || '0'} 
                icon={Users}
              />
              <MetricItem 
                label="CPM" 
                value={`$${stage.avg_cpm?.toFixed(2) || '0'}`}
                status={stage.avg_cpm < 25 ? 'good' : 'warning'}
                icon={TrendingUp}
              />
              <MetricItem 
                label="Engagement Rate" 
                value={`${stage.engagement_rate?.toFixed(2) || 0}%`}
                status={stage.engagement_rate > 3 ? 'good' : 'warning'}
                icon={ThumbsUp}
              />
              <MetricItem 
                label="Saves" 
                value={stage.saves?.toLocaleString() || '0'}
                subtitle="quality signal!"
                icon={Bookmark}
              />
              <MetricItem 
                label="Video Views 3s+" 
                value={stage.video_views_3s?.toLocaleString() || '0'}
                subtitle="hook working"
                icon={Video}
              />
            </div>
            
            <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-950 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <p className="text-sm font-medium text-foreground">⚠️ Não calcular ROAS aqui - awareness não vende direto</p>
              <p className="text-xs text-muted-foreground mt-1">✅ Métricas de sucesso: CPM baixo + alto engagement</p>
            </div>
          </>
        )}

        {/* Consideration Metrics */}
        {stage.funnel_stage === 'consideration' && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <MetricItem 
                label="Leads" 
                value={stage.leads?.toLocaleString() || '0'}
                icon={Users}
              />
              <MetricItem 
                label="CPL" 
                value={`$${stage.cpl?.toFixed(2) || '0'}`}
                status={stage.cpl < 5 ? 'good' : 'warning'}
                icon={TrendingUp}
              />
              <MetricItem 
                label="Landing Page Views" 
                value={stage.landing_page_views?.toLocaleString() || '0'}
                icon={Eye}
              />
              <MetricItem 
                label="LP Conversion Rate" 
                value={`${stage.lp_conversion_rate?.toFixed(2) || 0}%`}
                status={stage.lp_conversion_rate > 30 ? 'good' : 'warning'}
                icon={Target}
              />
            </div>
            
            <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-950 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <p className="text-sm font-medium text-foreground">⚠️ ROAS ainda não existe - leads não pagaram ainda</p>
              <p className="text-xs text-muted-foreground mt-1">✅ Métricas de sucesso: CPL baixo + LP conversion alta</p>
            </div>
          </>
        )}

        {/* Conversion Metrics */}
        {stage.funnel_stage === 'conversion' && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <MetricItem 
                label="Purchases" 
                value={stage.purchases?.toLocaleString() || '0'}
                icon={ShoppingCart}
              />
              <MetricItem 
                label="CAC (bottom only)" 
                value={`$${stage.cac_bottom_only?.toFixed(2) || '0'}`}
                icon={TrendingUp}
              />
              <MetricItem 
                label="Revenue" 
                value={`$${stage.revenue?.toLocaleString() || '0'}`}
                icon={Target}
              />
            </div>
            
            <div className="mt-4 p-4 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
              <p className="text-sm font-medium text-foreground">✅ HERE we have ROAS! But it's not the real ROAS...</p>
              <p className="text-xs text-muted-foreground mt-1">⚠️ These clients came from top/middle funnel too!</p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function MetricItem({ label, value, subtitle, status, icon: Icon }) {
  const statusColors = {
    good: 'text-green-600 dark:text-green-400',
    warning: 'text-orange-600 dark:text-orange-400',
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
      </div>
      <p className={`text-2xl font-bold ${status ? statusColors[status] : 'text-foreground'}`}>
        {value}
      </p>
      {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
    </div>
  );
}
